import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const c = {
  teal: '#00C9A7',
  navy: '#0F2033',
  border: '#E5EAF0',
  bg: '#F7F9FC',
  white: '#FFFFFF',
  text: '#1A2B3C',
  muted: '#6B7A8D',
  red: '#EF4444',
  green: '#22C55E',
  amber: '#F59E0B',
};

const NAV = [
  { id: 'overview', label: 'Overview', icon: '▦' },
  { id: 'inventory', label: 'Inventory', icon: '🚗' },
  { id: 'leads', label: 'Leads', icon: '👤' },
  { id: 'analytics', label: 'Analytics', icon: '📊' },
  { id: 'profile', label: 'Profile', icon: '🏢' },
];

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color }) {
  return (
    <div
      style={{
        background: c.white,
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        padding: '20px 24px',
        flex: 1,
        minWidth: 160,
      }}
    >
      <div style={{ fontSize: 13, color: c.muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: color || c.navy, lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: c.muted, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function Overview({ stats, onNavigate }) {
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: c.navy, margin: '0 0 20px' }}>Overview</h2>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
        <StatCard
          label="Active Listings"
          value={stats.active}
          sub="Live on marketplace"
          color={c.teal}
        />
        <StatCard label="Draft Listings" value={stats.draft} sub="Pending review" color={c.amber} />
        <StatCard label="Sold / Closed" value={stats.sold} sub="This month" color={c.muted} />
        <StatCard label="Total Leads" value={stats.leads} sub="Inquiries received" color={c.navy} />
      </div>
      <div
        style={{
          background: c.white,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          padding: 24,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: c.navy, marginBottom: 16 }}>
          Quick Actions
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => onNavigate('inventory', 'add')}
            style={{
              background: c.teal,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + Add Single Vehicle
          </button>
          <button
            onClick={() => onNavigate('inventory', 'upload')}
            style={{
              background: c.navy,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ⬆ Bulk Upload CSV
          </button>
          <button
            onClick={() => onNavigate('leads')}
            style={{
              background: c.muted,
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            📋 View All Leads
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inventory Tab ──────────────────────────────────────────────────────────────
function Inventory({ listings, onRefresh, dealerId, dealerName, initAction, onInitDone }) {
  const [tab, setTab] = useState('active');
  const [uploading, setUp] = useState(false);
  const [uploadMsg, setMsg] = useState('');
  const [showForm, setForm] = useState(false);
  const [dragging, setDrag] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (initAction === 'add') {
      setForm(true);
      onInitDone?.();
    }
    if (initAction === 'upload') {
      fileRef.current?.click();
      onInitDone?.();
    }
  }, [initAction]);

  const filtered = listings.filter(l => {
    if (tab === 'active') return l.attributes?.state === 'published';
    if (tab === 'draft')
      return l.attributes?.state === 'draft' || l.attributes?.state === 'pendingApproval';
    if (tab === 'sold') return l.attributes?.state === 'closed';
    return true;
  });

  async function handleCSV(file) {
    if (!file || !file.name.endsWith('.csv')) {
      setMsg('Please upload a .csv file');
      return;
    }
    setUp(true);
    setMsg('Uploading…');
    try {
      const text = await file.text();
      const resp = await fetch('/api/dealer-inventory/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv: text, dealerId, dealerName: dealerName || '' }),
      });
      const data = await resp.json();
      if (resp.ok) {
        const skippedMsg =
          data.skipped > 0 ? ` (${data.skipped} skipped — 1,000 listing cap reached)` : '';
        setMsg(`✅ ${data.created?.length || 0} listings created${skippedMsg}`);
      } else {
        setMsg(`❌ ${data.error || 'Upload failed'}`);
      }
      if (resp.ok) onRefresh();
    } catch (e) {
      setMsg('❌ ' + e.message);
    }
    setUp(false);
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 700, color: c.navy, margin: 0 }}>Inventory</h2>
        <button
          onClick={() => setForm(!showForm)}
          style={{
            background: c.teal,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '9px 18px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + Add Vehicle
        </button>
      </div>

      {/* CSV Drop Zone */}
      <div
        onDragOver={e => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => {
          e.preventDefault();
          setDrag(false);
          handleCSV(e.dataTransfer.files[0]);
        }}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? c.teal : c.border}`,
          borderRadius: 14,
          padding: '28px 20px',
          textAlign: 'center',
          background: dragging ? '#F0FDF9' : c.white,
          cursor: 'pointer',
          marginBottom: 16,
          transition: 'all 0.2s',
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 8 }}>⬆</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: c.navy }}>
          Drag & drop your CSV file here
        </div>
        <div style={{ fontSize: 12, color: c.muted, marginTop: 4 }}>
          or click to browse — Year, Make, Model, Price, Mileage, VIN
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={e => handleCSV(e.target.files[0])}
        />
      </div>

      {uploadMsg && (
        <div
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            background: uploadMsg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2',
            color: uploadMsg.startsWith('✅') ? '#166534' : '#991B1B',
            border: `1px solid ${uploadMsg.startsWith('✅') ? '#BBF7D0' : '#FECACA'}`,
          }}
        >
          {uploadMsg}
        </div>
      )}

      {/* CSV Template Download */}
      <div style={{ marginBottom: 20 }}>
        <a
          href="data:text/csv;charset=utf-8,year,make,model,trim,price,mileage,vin,color,fuel_type,transmission,description%0A2024,Ford,F-150,XLT,34999,12000,1FTFW1E50MFA00001,White,Gasoline,Automatic,Clean title one owner"
          download="inventory-template.csv"
          style={{ fontSize: 12, color: c.teal, textDecoration: 'none', fontWeight: 600 }}
        >
          ↓ Download CSV template
        </a>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 16,
          borderBottom: `1px solid ${c.border}`,
          paddingBottom: 0,
        }}
      >
        {['active', 'draft', 'sold'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 18px',
              fontSize: 13,
              fontWeight: tab === t ? 700 : 400,
              color: tab === t ? c.teal : c.muted,
              borderBottom: tab === t ? `2px solid ${c.teal}` : '2px solid transparent',
              cursor: 'pointer',
              textTransform: 'capitalize',
              marginBottom: -1,
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Listing Table */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: c.muted, fontSize: 14 }}>
          No {tab} listings yet.
        </div>
      ) : (
        <div
          style={{
            background: c.white,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: c.bg }}>
                {['Vehicle', 'Price', 'Mileage', 'Status', 'Created', ''].map(h => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: c.muted,
                      fontSize: 12,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => {
                const attr = l.attributes || {};
                const title = attr.title || 'Untitled';
                const price = attr.price?.amount
                  ? `$${(attr.price.amount / 100).toLocaleString()}`
                  : '—';
                const km = attr.publicData?.mileage
                  ? `${Number(attr.publicData.mileage).toLocaleString()} km`
                  : '—';
                const state = attr.state || '—';
                const date = attr.createdAt
                  ? new Date(attr.createdAt).toLocaleDateString('en-CA')
                  : '—';
                return (
                  <tr key={l.id?.uuid || i} style={{ borderTop: `1px solid ${c.border}` }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: c.navy }}>
                      {title}
                    </td>
                    <td style={{ padding: '12px 16px', color: c.text }}>{price}</td>
                    <td style={{ padding: '12px 16px', color: c.muted }}>{km}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          background:
                            state === 'published'
                              ? '#F0FDF4'
                              : state === 'draft'
                              ? '#FFFBEB'
                              : '#F1F5F9',
                          color:
                            state === 'published'
                              ? '#166534'
                              : state === 'draft'
                              ? '#92400E'
                              : c.muted,
                          borderRadius: 20,
                          padding: '3px 10px',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {state}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: c.muted }}>{date}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        style={{
                          background: 'none',
                          border: `1px solid ${c.border}`,
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 12,
                          cursor: 'pointer',
                          color: c.muted,
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual Add Form */}
      {showForm && (
        <AddVehicleForm
          onClose={() => setForm(false)}
          onSuccess={() => {
            setForm(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

// ── Add Vehicle Form ───────────────────────────────────────────────────────────
function AddVehicleForm({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    price: '',
    mileage: '',
    vin: '',
    color: '',
    fuel_type: 'Gasoline',
    transmission: 'Automatic',
    description: '',
  });
  const [saving, setSave] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    setSave(true);
    setErr('');
    try {
      const resp = await fetch('/api/dealer-inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed');
      onSuccess();
    } catch (e) {
      setErr(e.message);
    }
    setSave(false);
  }

  const field = (key, label, type = 'text', opts = null) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: c.muted }}>{label}</label>
      {opts ? (
        <select
          value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            border: `1px solid ${c.border}`,
            fontSize: 13,
            color: c.text,
          }}
        >
          {opts.map(o => (
            <option key={o}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            border: `1px solid ${c.border}`,
            fontSize: 13,
            color: c.text,
            outline: 'none',
          }}
        />
      )}
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          background: c.white,
          borderRadius: 18,
          padding: 32,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: c.navy }}>Add Vehicle</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: c.muted,
            }}
          >
            ✕
          </button>
        </div>
        <form
          onSubmit={submit}
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}
        >
          {field('year', 'Year', 'number')}
          {field('make', 'Make')}
          {field('model', 'Model')}
          {field('trim', 'Trim')}
          {field('price', 'Price ($)', 'number')}
          {field('mileage', 'Mileage (km)', 'number')}
          {field('vin', 'VIN')}
          {field('color', 'Color')}
          {field('fuel_type', 'Fuel Type', 'text', ['Gasoline', 'Diesel', 'Hybrid', 'Electric'])}
          {field('transmission', 'Transmission', 'text', ['Automatic', 'Manual', 'CVT'])}
          <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: c.muted }}>Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                fontSize: 13,
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          {err && <div style={{ gridColumn: '1/-1', color: c.red, fontSize: 13 }}>{err}</div>}
          <div style={{ gridColumn: '1/-1', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: 'none',
                fontSize: 13,
                cursor: 'pointer',
                color: c.muted,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '10px 24px',
                borderRadius: 8,
                background: c.teal,
                border: 'none',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saving ? 'Saving…' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Leads Tab ──────────────────────────────────────────────────────────────────
function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoad] = useState(true);

  useEffect(() => {
    fetch('/api/dealer-leads')
      .then(r => r.json())
      .then(d => {
        setLeads(d.leads || []);
        setLoad(false);
      })
      .catch(() => setLoad(false));
  }, []);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: c.navy, margin: '0 0 20px' }}>Leads</h2>
      {loading ? (
        <div style={{ color: c.muted, fontSize: 14 }}>Loading…</div>
      ) : leads.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: c.muted, fontSize: 14 }}>
          No leads yet. They'll appear here when buyers inquire.
        </div>
      ) : (
        <div
          style={{
            background: c.white,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: c.bg }}>
                {['Name', 'Vehicle of Interest', 'Contact', 'Date', 'Status'].map(h => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 16px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: c.muted,
                      fontSize: 12,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: c.navy }}>
                    {lead.name || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: c.text }}>{lead.vehicle || '—'}</td>
                  <td style={{ padding: '12px 16px', color: c.muted }}>
                    {lead.email || lead.phone || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', color: c.muted }}>
                    {lead.date ? new Date(lead.date).toLocaleDateString('en-CA') : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span
                      style={{
                        background: '#F0FDF4',
                        color: '#166534',
                        borderRadius: 20,
                        padding: '3px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      New
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Analytics Tab ──────────────────────────────────────────────────────────────
function Analytics({ stats }) {
  const bars = [
    { label: 'Views', value: stats.views || 0, max: 500, color: c.teal },
    { label: 'Leads', value: stats.leads || 0, max: 100, color: c.navy },
    { label: 'Active', value: stats.active || 0, max: 50, color: c.amber },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: c.navy, margin: '0 0 20px' }}>
        Analytics
      </h2>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard
          label="Total Views"
          value={(stats.views || 0).toLocaleString()}
          sub="Last 30 days"
          color={c.teal}
        />
        <StatCard label="Avg. Days on Lot" value={stats.avgDays || '—'} sub="Time to inquiry" />
        <StatCard label="Lead Conversion" value={stats.conversion || '—'} sub="Views → inquiries" />
      </div>
      <div
        style={{
          background: c.white,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          padding: 24,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: c.navy, marginBottom: 20 }}>
          Performance
        </div>
        {bars.map(b => (
          <div key={b.label} style={{ marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: c.muted,
                marginBottom: 6,
              }}
            >
              <span>{b.label}</span>
              <span style={{ fontWeight: 700, color: c.navy }}>{b.value}</span>
            </div>
            <div style={{ background: c.bg, borderRadius: 99, height: 8, overflow: 'hidden' }}>
              <div
                style={{
                  width: `${Math.min((b.value / b.max) * 100, 100)}%`,
                  background: b.color,
                  height: '100%',
                  borderRadius: 99,
                  transition: 'width 0.6s ease',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Profile Tab ────────────────────────────────────────────────────────────────
const PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

function Profile({ dealerId }) {
  const EMPTY = {
    dealerName: '',
    city: '',
    province: 'ON',
    phone: '',
    email: '',
    website: '',
    description: '',
    licenceNumber: '',
    established: '',
    dealerLogo: '',
  };
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoad] = useState(true);
  const [saving, setSave] = useState(false);
  const [msg, setMsg] = useState('');
  const logoRef = useRef();

  useEffect(() => {
    if (!dealerId) {
      setLoad(false);
      return;
    }
    fetch(`/api/dealer-profile?dealerId=${encodeURIComponent(dealerId)}`)
      .then(r => r.json())
      .then(d => {
        if (d.profile) setForm(p => ({ ...EMPTY, ...d.profile }));
        setLoad(false);
      })
      .catch(() => setLoad(false));
  }, [dealerId]);

  async function save(e) {
    e.preventDefault();
    setSave(true);
    setMsg('');
    try {
      const resp = await fetch('/api/dealer-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealerId, profile: form }),
      });
      const data = await resp.json();
      setMsg(resp.ok ? '✅ Profile saved' : `❌ ${data.error || 'Save failed'}`);
    } catch {
      setMsg('❌ Network error');
    }
    setSave(false);
  }

  function handleLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      setMsg('❌ Logo must be under 500 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, dealerLogo: ev.target.result }));
    reader.readAsDataURL(file);
  }

  const inp = (key, label, type = 'text', placeholder = '') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: c.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{
          padding: '10px 12px',
          borderRadius: 8,
          border: `1px solid ${c.border}`,
          fontSize: 14,
          color: c.text,
          outline: 'none',
          fontFamily: 'inherit',
          background: c.white,
        }}
      />
    </div>
  );

  if (loading)
    return <div style={{ color: c.muted, padding: 40, textAlign: 'center' }}>Loading profile…</div>;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 700, color: c.navy, margin: 0 }}>
          Dealership Profile
        </h2>
        {form.dealerName && (
          <a
            href={`/dealers/${encodeURIComponent(dealerId)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: c.teal, fontWeight: 600, textDecoration: 'none' }}
          >
            View Channel Page →
          </a>
        )}
      </div>

      <form onSubmit={save}>
        {/* Logo */}
        <div
          style={{
            background: c.white,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: 24,
            marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: c.navy, marginBottom: 16 }}>
            Dealership Logo
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: 14,
                border: `2px dashed ${c.border}`,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: c.bg,
                flexShrink: 0,
              }}
            >
              {form.dealerLogo ? (
                <img
                  src={form.dealerLogo}
                  alt="Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: 28 }}>🏢</span>
              )}
            </div>
            <div>
              <button
                type="button"
                onClick={() => logoRef.current?.click()}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: `1px solid ${c.border}`,
                  background: c.white,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: c.navy,
                  marginBottom: 6,
                  display: 'block',
                }}
              >
                Upload Logo
              </button>
              <div style={{ fontSize: 11, color: c.muted }}>
                PNG or JPG · max 500 KB · square recommended
              </div>
              <input
                ref={logoRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={handleLogo}
              />
              {form.dealerLogo && (
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, dealerLogo: '' }))}
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: c.red,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div
          style={{
            background: c.white,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: 24,
            marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: c.navy, marginBottom: 16 }}>
            Basic Information
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {inp('dealerName', 'Dealership Name', 'text', 'Sunrise Motors Inc.')}
            {inp('licenceNumber', 'Dealer Licence #', 'text', 'MVDA-12345')}
            {inp('established', 'Year Established', 'number', '2001')}
            {inp('phone', 'Phone', 'tel', '+1 (204) 555-0100')}
            {inp('email', 'Public Email', 'email', 'info@sunrisemotors.ca')}
            {inp('website', 'Website', 'url', 'https://sunrisemotors.ca')}
          </div>
        </div>

        {/* Location */}
        <div
          style={{
            background: c.white,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: 24,
            marginBottom: 20,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: c.navy, marginBottom: 16 }}>
            Location
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {inp('city', 'City', 'text', 'Winnipeg')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: c.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Province
              </label>
              <select
                value={form.province}
                onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: `1px solid ${c.border}`,
                  fontSize: 14,
                  color: c.text,
                  background: c.white,
                  fontFamily: 'inherit',
                }}
              >
                {PROVINCES.map(p => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Description */}
        <div
          style={{
            background: c.white,
            border: `1px solid ${c.border}`,
            borderRadius: 14,
            padding: 24,
            marginBottom: 24,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: c.navy, marginBottom: 16 }}>
            About Your Dealership
          </div>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: c.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 5,
              display: 'block',
            }}
          >
            Description
          </label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            rows={4}
            maxLength={500}
            placeholder="Tell buyers about your dealership — specialties, brands, awards, community involvement…"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              fontSize: 14,
              color: c.text,
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 11, color: c.muted, textAlign: 'right', marginTop: 4 }}>
            {form.description.length}/500
          </div>
        </div>

        {msg && (
          <div
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
              background: msg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2',
              color: msg.startsWith('✅') ? '#166534' : '#991B1B',
              border: `1px solid ${msg.startsWith('✅') ? '#BBF7D0' : '#FECACA'}`,
            }}
          >
            {msg}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          style={{
            background: saving ? c.muted : c.teal,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '12px 32px',
            fontSize: 14,
            fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </form>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function DealerDashboardPage() {
  const [activeTab, setTab] = useState('overview');
  const [initAction, setInitAction] = useState(null);
  const [listings, setList] = useState([]);
  const [loading, setLoad] = useState(true);
  const currentUser = useSelector(s => s.user?.currentUser);
  const userName =
    currentUser?.attributes?.profile?.displayName ||
    currentUser?.attributes?.profile?.firstName ||
    'Dealer';
  const dealerId = currentUser?.id?.uuid || '';

  const stats = {
    active: listings.filter(l => l.attributes?.state === 'published').length,
    draft: listings.filter(l => l.attributes?.state === 'draft').length,
    sold: listings.filter(l => l.attributes?.state === 'closed').length,
    leads: 0,
    views: 0,
    avgDays: '—',
    conversion: '—',
  };

  async function loadListings() {
    if (!dealerId) return;
    setLoad(true);
    try {
      const r = await fetch(`/api/dealer-inventory?dealerId=${encodeURIComponent(dealerId)}`);
      const d = await r.json();
      setList(d.listings || []);
    } catch {}
    setLoad(false);
  }

  useEffect(() => {
    if (dealerId) loadListings();
  }, [dealerId]);

  const content = () => {
    if (loading)
      return (
        <div style={{ color: c.muted, fontSize: 14, padding: 40, textAlign: 'center' }}>
          Loading your inventory…
        </div>
      );
    switch (activeTab) {
      case 'overview':
        return (
          <Overview
            stats={stats}
            onNavigate={(tab, action) => {
              setTab(tab);
              if (action === 'add') setInitAction('add');
              if (action === 'upload') setInitAction('upload');
            }}
          />
        );
      case 'inventory':
        return (
          <Inventory
            listings={listings}
            onRefresh={loadListings}
            dealerId={dealerId}
            dealerName={userName}
            initAction={initAction}
            onInitDone={() => setInitAction(null)}
          />
        );
      case 'leads':
        return <Leads />;
      case 'analytics':
        return <Analytics stats={stats} />;
      case 'profile':
        return <Profile dealerId={dealerId} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: c.bg,
        fontFamily: 'DM Sans, -apple-system, sans-serif',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          background: c.white,
          borderBottom: `1px solid ${c.border}`,
          padding: '0 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 60,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🏢</span>
          <span style={{ fontWeight: 700, fontSize: 16, color: c.navy }}>Dealer Portal</span>
        </div>
        <div style={{ fontSize: 13, color: c.muted }}>
          Welcome, <strong style={{ color: c.navy }}>{userName}</strong>
        </div>
      </div>

      <div
        style={{ display: 'flex', maxWidth: 1200, margin: '0 auto', padding: '32px 24px', gap: 28 }}
      >
        {/* Sidebar */}
        <div style={{ width: 200, flexShrink: 0 }}>
          <nav
            style={{
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: 14,
              overflow: 'hidden',
            }}
          >
            {NAV.map(n => (
              <button
                key={n.id}
                onClick={() => setTab(n.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '13px 18px',
                  border: 'none',
                  background: activeTab === n.id ? '#F0FDF9' : 'none',
                  color: activeTab === n.id ? c.teal : c.muted,
                  fontWeight: activeTab === n.id ? 700 : 400,
                  fontSize: 14,
                  cursor: 'pointer',
                  textAlign: 'left',
                  borderLeft: activeTab === n.id ? `3px solid ${c.teal}` : '3px solid transparent',
                }}
              >
                <span>{n.icon}</span>
                {n.label}
              </button>
            ))}
          </nav>
          <a
            href="/"
            style={{
              display: 'block',
              marginTop: 16,
              fontSize: 12,
              color: c.muted,
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            ← Back to marketplace
          </a>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>{content()}</div>
      </div>
    </div>
  );
}
